import React from 'react'

import NextImage from 'next/image'

import type { DatoImageData } from 'Data/types'

import get from 'lodash/get'

type Props = {
  alt?: string
  height?: number
  width?: number
  // Accepted and ignored. Both were react-datocms knobs; next/image lazy-loads
  // by default and `priority` is the only opt-out, so call sites still passing
  // `lazy`/`pictureStyle` keep type-checking and keep their current behaviour.
  lazy?: boolean
  layout?: 'fill' | 'responsive' | 'fixed' | 'raw' | 'intrinsic'
  image?: any
  pictureStyle?: any
  // Display-based srcset selection. Without it the migrated-image branch
  // falls back to Dato's intrinsic-width formula, which overserves badly
  // for fixed slots (a 172px logo slot would negotiate against the source's
  // full width).
  sizes?: string
  src?: string
  style?: any
  priority?: boolean
}

const Image = ({
  alt = 'Default image alt text',
  height,
  image,
  layout,
  priority,
  sizes,
  src,
  style = { maxWidth: 'unset' },
  width,
}: Props) => {
  const datoData: DatoImageData['data'] | undefined = get(image, 'data')
  const responsiveImage = get(image, 'responsiveImage')
  const imageUrl = get(image, 'url') || image

  // Migrated DatoCMS assets (src/Data) — statically imported, rendered with
  // next/image `fill` inside a wrapper that mirrors the old react-datocms
  // structure: same inline wrapper styles, aspect-ratio in place of the SVG
  // sizer, object-fit still controlled by each holder's CSS.
  if (datoData) {
    return (
      <div
        style={{
          overflow: 'hidden',
          position: 'relative',
          width: '100%',
          aspectRatio: `${datoData.width} / ${datoData.height}`,
          ...style,
        }}
      >
        <NextImage
          alt={get(image, 'alt') ?? ''}
          fill
          priority={priority}
          sizes={
            sizes ??
            `(max-width: ${datoData.width}px) 100vw, ${datoData.width}px`
          }
          src={datoData}
        />
      </div>
    )
  }

  // Blog imagery is the last content still hosted on DatoCMS. It used to render
  // through react-datocms, which hotlinks www.datocms-assets.com directly.
  //
  // The bytes were never the problem: Dato's imgix already answers with AVIF
  // (`auto=format`) at four sensible widths, and the featured image measures
  // 36 KB on the wire. The problem is that it is a *third origin*. Under
  // Lighthouse's simulated 4G (562 ms of modelled request latency) a fresh
  // DNS + TCP + TLS handshake costs more than the image itself, and the
  // `preconnect` on the blog routes does not help because the simulator does
  // not model preconnect. Worse, react-datocms emits no `<link rel=preload>`,
  // so even with fetchPriority=high the browser only discovers the LCP image
  // once it has parsed that far into the body.
  //
  // Routing it through next/image makes it same-origin, so it reuses the HTTP/2
  // connection that is already open, and `priority` emits a real preload.
  //
  // The wrapper reproduces react-datocms's own structure exactly, and for the
  // same reason as the migrated-asset branch above: `aspect-ratio` stands in
  // for the sizer <img> that used to give the wrapper its height. That sizer
  // is load-bearing — the blog *card* holder sets no height of its own and
  // derives it entirely from the image's ratio, while the hero holder has a
  // fixed height and stretches the wrapper instead. Both cases were verified
  // against the pre-change DOM at 412 and 1440 CSS px.
  if (responsiveImage) {
    return (
      <div
        style={{
          overflow: 'hidden',
          position: 'relative',
          width: '100%',
          aspectRatio: `${responsiveImage.width} / ${responsiveImage.height}`,
          ...style,
        }}
      >
        <NextImage
          alt={responsiveImage.alt ?? ''}
          fill
          priority={priority}
          sizes={sizes ?? responsiveImage.sizes ?? '100vw'}
          src={responsiveImage.src}
        />
      </div>
    )
  }

  if (imageUrl || src) {
    return (
      <NextImage
        alt={get(image, 'alt') ?? alt}
        height={height}
        layout={layout}
        priority={priority}
        sizes={sizes}
        src={imageUrl || src}
        style={style}
        width={width}
      />
    )
  }

  return null
}

export default Image
